import { Button, Col, Modal, UploadProps } from 'antd';
import React, { Dispatch, useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import api from '../../service/api';
import { ApplicationState } from '../../store';
import { SKW_AUTH, SKW_PREFIX, SKW_URL } from '../../utils/constants';
import { SaveOutlined } from '@ant-design/icons';
import { Action } from 'typesafe-actions';
import { loadRequest } from '../../store/ducks/pedidoEntrega/actions';
import mask, { formatValueAsQuant, maskCapitalized } from '../../utils/mask';
import { IPedidoReq } from '../../store/ducks/pedidoEntrega/types';
import { FieldData } from './components/Form/props';
import CustomizedForm from './components/Form';
import Spin from '../Spin';
import { rejects } from 'assert';
import TitlePage from 'components/TitlePage';
import Dragger from 'antd/lib/upload/Dragger';

interface Props {
  clienteRetira: boolean;
  selectedTranportadora: any;
}

const Xml: React.FC<Props> = ({ clienteRetira, selectedTranportadora }) => {
  const dispatch = useDispatch<Dispatch<Action>>();
  const cddAtivo = useSelector((state: ApplicationState) => state.auth.cddAtivo);
  const [loadingXml, setLoadingXml] = useState<boolean>(false);
  const [visible, setVisible] = useState(false);
  const token = useSelector((state: ApplicationState) => state.auth.token);
  const loading = useSelector(
    (state: ApplicationState) => state.PedidoEntrega.loadingPedidoEntrega
  );

  const [fields, setFields] = useState<FieldData[]>([{ name: [''], value: '' }]);
  const [chaveNfe, setChaveNfe] = useState<string>('');
  const [codTomador, setCodTomador] = useState<string>('');
  const [nomeTomador, setNomeTomador] = useState<string>('');
  const [codFornecedor, setCodFornecedor] = useState<string>('');
  const [nomeFornecedor, setNomeFornecedor] = useState<string>('');
  const [valor, setValor] = useState<string>('');
  const [numNota, setNumNota] = useState<string>('');
  const [pesoBruto, setPesoBruto] = useState<string>('');
  const [codCidadeOrigem, setCodCidadeOrigem] = useState<string>('');
  const [observacao, setObservacao] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [destinatario, setDestinatario] = useState<string>('');
  const [tipoVolume, setTipoVolume] = useState<string>('');
  const [qtdVolume, setQtdVolume] = useState<string>('');
  const [nomeCidadeOrigem, setNomeCidadeOrigem] = useState<string>('');

  useEffect(() => {
    const chaveNfeFormated = chaveNfe ? chaveNfe : undefined;
    const nomeTomadorFormated = nomeTomador ? maskCapitalized(nomeTomador as string) : undefined;
    const nomeForncedorFormated = nomeFornecedor
      ? maskCapitalized(nomeFornecedor as string)
      : undefined;
    const tipoVolumeFormated = tipoVolume ? mask('camelCase', tipoVolume) : undefined;
    const qtdVolumeFormated = qtdVolume ? formatValueAsQuant(qtdVolume) : undefined;
    const numNotaFormated = numNota ? numNota : undefined;
    const pesoBrutoFormated = pesoBruto ? formatarPeso(pesoBruto) : undefined;
    const valorFormated = valor ? formatarParaBRL(+valor) : undefined;
    const nomeCidadeOrigemFormated = nomeCidadeOrigem
      ? mask('camelCase', nomeCidadeOrigem as string)
      : undefined;
    const observacaoFormated = observacao ? maskCapitalized(observacao as string) : undefined;

    setFields([
      { name: ['chaveNfe'], value: chaveNfeFormated },
      { name: ['nometomador'], value: nomeTomadorFormated },
      { name: ['nomeFornecedor'], value: nomeForncedorFormated },
      { name: ['tipoVolume'], value: tipoVolumeFormated },
      { name: ['qtdVolume'], value: qtdVolumeFormated },
      { name: ['numNota'], value: numNotaFormated },
      { name: ['pesoBruto'], value: pesoBrutoFormated },
      { name: ['valor'], value: valorFormated },
      { name: ['nomeCidadeOrigem'], value: nomeCidadeOrigemFormated },
      { name: ['observacao'], value: observacaoFormated },
    ]);
  }, [
    chaveNfe,
    nomeFornecedor,
    nomeTomador,
    tipoVolume,
    qtdVolume,
    numNota,
    pesoBruto,
    valor,
    nomeCidadeOrigem,
    observacao,
  ]);

  const props: UploadProps = {
    accept: '.xml',
    name: 'prototipo',
    action: `${SKW_URL}/upload?serviceName=${SKW_PREFIX}criaPedidoEntrega&accessKey=${SKW_AUTH}`,
    onChange(info: any) { },
    showUploadList: {
      showRemoveIcon: false,
    },

    beforeUpload(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const img = document.createElement('img');
          img.src = reader.result as string;

          function reqListener(this: any) {
            setLoadingXml(true);
          }

          function transferFailed() {
            setLoadingXml(false);
            setVisible(false);
            Swal.fire({
              title: 'Erro ao importar o xml!',
              icon: 'error',
              showConfirmButton: true,
            }).then((result) => {
              setVisible(false);
            });
          }

          async function transferComplete(this: any) {
            try {
              const nomeTomador = await this.responseXML
                ?.getElementsByTagName('dest')[0]
                ?.getElementsByTagName('xNome')[0]?.textContent;

              const cnpjCpfTomador =
                (await this.responseXML
                  ?.getElementsByTagName('dest')[0]
                  ?.getElementsByTagName('CNPJ')[0]?.textContent) ??
                this.responseXML?.getElementsByTagName('dest')[0]?.getElementsByTagName('CPF')[0]
                  ?.textContent;

              const codCidadeTomador = await this.responseXML
                ?.getElementsByTagName('dest')[0]
                ?.getElementsByTagName('enderDest')[0]
                ?.getElementsByTagName('cMun')[0]?.textContent;

              const inscricaoEstadualTomador =
                (await this.responseXML
                  ?.getElementsByTagName('dest')[0]
                  ?.getElementsByTagName('IE')[0]?.textContent) || 'isento';

              const nomeFornecedor = await this.responseXML
                ?.getElementsByTagName('emit')[0]
                ?.getElementsByTagName('xNome')[0]?.textContent;

              const cnpjCpfFornecedor =
                (await this.responseXML
                  ?.getElementsByTagName('emit')[0]
                  ?.getElementsByTagName('CNPJ')[0]?.textContent) ??
                this.responseXML?.getElementsByTagName('emit')[0]?.getElementsByTagName('CPF')[0]
                  ?.textContent;

              const codCidadeFornecedor = await this.responseXML
                ?.getElementsByTagName('emit')[0]
                ?.getElementsByTagName('enderEmit')[0]
                ?.getElementsByTagName('cMun')[0]?.textContent;

              const inscricaoEstadualFornecedor =
                (await this.responseXML
                  ?.getElementsByTagName('emit')[0]
                  ?.getElementsByTagName('IE')[0]?.textContent) || 'isento';

              const codCidade = await this.responseXML
                ?.getElementsByTagName('ide')[0]
                ?.getElementsByTagName('cMunFG')[0]?.textContent;

              await api
                .post(`call?serviceName=${SKW_PREFIX}cadastraParceiroXML&token=${token}`, {
                  tomador: {
                    nome: nomeTomador,
                    cnpjCpf: cnpjCpfTomador,
                    codCidade: codCidadeTomador,
                    inscricaoEstadual: inscricaoEstadualTomador,
                  },
                  fornecedor: {
                    nome: nomeFornecedor,
                    cnpjCpf: cnpjCpfFornecedor,
                    codCidade: codCidadeFornecedor,
                    inscricaoEstadual: inscricaoEstadualFornecedor,
                  },
                  codCidade,
                })

                .then(async (res: any) => {
                  const chaveNfeXml = await this.responseXML
                    ?.getElementsByTagName('protNFe')[0]
                    ?.getElementsByTagName('chNFe')[0]?.textContent;
                  const codTomadorXml = await res?.data?.tomador.codParc;
                  const nomeTomadorXml = await res?.data?.tomador?.nome;
                  const codFornecedorXml = await res?.data?.fornecedor.codParc;
                  const nomeFornecedorXml = await res?.data?.fornecedor?.nome;
                  const valorXml = await this.responseXML
                    ?.getElementsByTagName('ICMSTot')[0]
                    ?.getElementsByTagName('vNF')[0]?.textContent;
                  const numNotaXml = await this.responseXML
                    ?.getElementsByTagName('ide')[0]
                    ?.getElementsByTagName('nNF')[0]?.textContent;
                  const pesoBrutoXml = await this.responseXML
                    ?.getElementsByTagName('transp')[0]
                    ?.getElementsByTagName('pesoB')[0]?.textContent;
                  const codCidadeOrigemXml = await res?.data?.cidade.codCid;
                  const nomeCidadeOrigemXml = `${res?.data?.cidade?.nome?.toString()} - ${res?.data?.cidade?.siglaEstado?.toString()}`;
                  const observacaoXml = await this.responseXML
                    ?.getElementsByTagName('infAdic')[0]
                    ?.getElementsByTagName('infCpl')[0]
                    ?.textContent?.toString();
                  const destinatarioXml = await res?.data?.tomador.codParc;
                  const tipoVolumeXml = await this.responseXML
                    ?.getElementsByTagName('transp')[0]
                    ?.getElementsByTagName('esp')[0]
                    ?.textContent?.toString();
                  const qtdVolumeXml = await this.responseXML
                    ?.getElementsByTagName('transp')[0]
                    ?.getElementsByTagName('qVol')[0]?.textContent;

                  chaveNfeXml && setChaveNfe(chaveNfeXml);
                  codTomadorXml && setCodTomador(codTomadorXml.toString());
                  nomeTomadorXml && setNomeTomador(nomeTomadorXml);
                  codFornecedorXml && setCodFornecedor(codFornecedorXml.toString());
                  nomeFornecedorXml && setNomeFornecedor(nomeFornecedorXml);
                  valorXml && setValor(valorXml);
                  numNotaXml && setNumNota(numNotaXml);
                  pesoBrutoXml && setPesoBruto(pesoBrutoXml);
                  codCidadeOrigemXml && setCodCidadeOrigem(codCidadeOrigemXml.toString());
                  nomeCidadeOrigemXml && setNomeCidadeOrigem(nomeCidadeOrigemXml);
                  observacaoXml && setObservacao(observacaoXml);
                  destinatarioXml && setDestinatario(destinatarioXml.toString());
                  tipoVolumeXml && setTipoVolume(tipoVolumeXml);
                  qtdVolumeXml && setQtdVolume(qtdVolumeXml);

                  setLoadingXml(false);
                  setVisible(true);
                })
                .catch((error: any) => {
                  if (error.response.data.Erro) {
                    Swal.fire({
                      title: 'Um erro ocorreu ao tentar importar o XML!',
                      html: error.response.data.Erro,
                      icon: 'error',
                      showConfirmButton: true,
                    }).then((result) => {
                      setVisible(false);
                    });
                  } else {
                    Swal.fire({
                      title: 'Um erro ocorreu ao tentar importar o XML!',
                      icon: 'error',
                      showConfirmButton: true,
                    }).then((result) => {
                      setVisible(false);
                    });
                  }
                });
              resolve();
            } catch (error: any) {
              rejects(error);
              if (error?.response?.data?.Erro) {
                Swal.fire({
                  title: 'Um erro ocorreu ao tentar importar o XML!',
                  html: error.response.data.Erro,
                  icon: 'error',
                  showConfirmButton: true,
                }).then((result) => {
                  setVisible(false);
                });
              } else {
                Swal.fire({
                  title: 'Um erro ocorreu ao tentar importar o XML!',
                  icon: 'error',
                  showConfirmButton: true,
                }).then((result) => {
                  setVisible(false);
                });
              }
            }
          }

          var oReq = new XMLHttpRequest();
          oReq.addEventListener('load', transferComplete, false);
          oReq.addEventListener('error', transferFailed, false);

          oReq.onload = reqListener;
          oReq.open('get', img.src, true);
          oReq.send();
        };
      });
    },
  };

  function formatarPeso(numeroDigitado: string) {
    const valorLimpo = numeroDigitado?.replace(/[^\d.]/g, '');
    const partes = valorLimpo?.split('.');
    let parteInteira = partes[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    if (partes.length === 2) {
      return parteInteira + ',' + partes[1];
    } else {
      return parteInteira;
    }
  }

  function formatarParaBRL(valor: number): string {
    const valorformatado = Number(valor?.toString()?.replace(/[^0-9.,]/g, ''));
    return valorformatado.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  const handleAdd = (values: any) => {
    const pedido: IPedidoReq = {
      chaveNfe: chaveNfe,
      codCdd: Number(cddAtivo?.codCdd),
      codDestinatario: Number(codTomador),
      codFornecedor: Number(codFornecedor),
      codTomador: Number(codTomador),
      codTransportadora: Number(selectedTranportadora),
      clienteEntrega: clienteRetira,
      valor: Number(valor),
      numNota: Number(numNota),
      pesoBruto: Number(pesoBruto),
      codCidadeOrigem: Number(codCidadeOrigem),
      observacao: observacao,
      tipoVolume: tipoVolume ?? 'Diverso',
      qtdVolume: Number(qtdVolume),
    };
    dispatch(loadRequest({ pedido }));
    setVisible(false);
  };

  function handleCancel() {
    setVisible(false);
    setChaveNfe('');
    setCodTomador('');
    setValor('');
    setNumNota('');
    setPesoBruto('');
    setCodCidadeOrigem('');
    setObservacao('');
    setCodFornecedor('');
    setTipoVolume('');
    setQtdVolume('');
    setNomeTomador('');
    setNomeFornecedor('');
    setDestinatario('');
    setNomeCidadeOrigem('');
  }

  return (
    <>
      <Dragger
        {...props}
        style={{
          margin: 0,
          padding: 0,
          backgroundColor: 'none',
          border: 'none',
          width: '100%',
        }}
        defaultFileList={[]}
        fileList={[]}
      >
        <Button
          data-cy="btnImportXml"
          type="ghost"
          icon={<UploadOutlined />}
          style={{ width: '100%' }}
        >
          Importar XML
        </Button>
      </Dragger>

      <Modal
        title={<TitlePage>Adicionar documento</TitlePage>}
        centered
        visible={visible}
        onOk={() => setVisible(false)}
        onCancel={() => handleCancel()}
        width={850}
        footer={[]}
      >
        <div className="boxinputs">
          {loadingXml ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spin color="#1890ff" />
            </div>
          ) : (
            <CustomizedForm
              setFields={setFields}
              fields={fields}
              onChange={(newFields) => {
                setFields(newFields);
              }}
              setChaveNfe={setChaveNfe}
              setCodTomador={setCodTomador}
              setNomeFornecedor={setNomeFornecedor}
              setValor={setValor}
              setNumNota={setNumNota}
              setPesoBruto={setPesoBruto}
              setCodCidadeOrigem={setCodCidadeOrigem}
              setObservacao={setObservacao}
              setCodFornecedor={setCodFornecedor}
              setTipoVolume={setTipoVolume}
              setQtdVolume={setQtdVolume}
              nometomador={nomeTomador}
              setNomeTomador={setNomeTomador}
              setNomeCidadeOrigem={setNomeCidadeOrigem}
            />
          )}

          {/* {<pre className="language-bash">{JSON.stringify(fields, null, 2)}</pre>} */}
        </div>
        <Col style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            data-cy="btnSalvar"
            type="primary"
            htmlType="submit"
            block
            icon={<SaveOutlined />}
            style={{ width: 'auto' }}
            onClick={(e) => handleAdd(e)}
            loading={loading}
            disabled={loadingXml}
          >
            Salvar
          </Button>
        </Col>
      </Modal>
    </>
  );
};

export default Xml;
